import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link2,
  Heading2,
  Heading3,
  Quote,
  Undo,
  Redo,
  Code,
  Eye,
} from 'lucide-react'
import { useCallback, useState, useEffect } from 'react'

interface BlogContentEditorProps {
  content: string
  onChange: (content: string) => void
  placeholder?: string
}

export function BlogContentEditor({
  content,
  onChange,
  placeholder = 'Write your blog post content...',
}: BlogContentEditorProps) {
  const [isHtmlMode, setIsHtmlMode] = useState(false)
  const [htmlContent, setHtmlContent] = useState(content)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-[var(--primary)] underline',
        },
      }),
    ],
    content: content,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[300px] p-4 focus:outline-none text-[var(--text-main)] prose-headings:text-[var(--text-main)] prose-p:text-[var(--text-main)] prose-strong:text-[var(--text-main)] prose-li:text-[var(--text-main)] prose-blockquote:text-[var(--text-muted)] prose-blockquote:border-[var(--border)]',
      },
    },
    onUpdate: ({ editor }) => {
      if (!isHtmlMode) {
        const html = editor.getHTML()
        setHtmlContent(html)
        onChange(html)
      }
    },
  })

  // Sync content when switching modes
  const toggleMode = useCallback(() => {
    if (isHtmlMode) {
      // Switching from HTML to Visual - update editor with HTML content
      editor?.commands.setContent(htmlContent)
    } else {
      // Switching from Visual to HTML - get content from editor
      if (editor) {
        setHtmlContent(editor.getHTML())
      }
    }
    setIsHtmlMode(!isHtmlMode)
  }, [isHtmlMode, htmlContent, editor])

  // Handle HTML textarea changes
  const handleHtmlChange = useCallback((value: string) => {
    setHtmlContent(value)
    onChange(value)
  }, [onChange])

  const setLink = useCallback(() => {
    if (!editor) return

    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('Enter URL:', previousUrl)

    if (url === null) return

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }, [editor])

  if (!editor) {
    return null
  }

  return (
    <div className="border border-[var(--border)] rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="border-b border-[var(--border)] bg-[var(--bg-element)] p-2 flex flex-wrap gap-1">
        {!isHtmlMode && (
          <>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive('bold')}
              title="Bold"
            >
              <Bold className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive('italic')}
              title="Italic"
            >
              <Italic className="h-4 w-4" />
            </ToolbarButton>
            <div className="w-px h-6 bg-[var(--border)] mx-1" />
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              isActive={editor.isActive('heading', { level: 2 })}
              title="Heading 2"
            >
              <Heading2 className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              isActive={editor.isActive('heading', { level: 3 })}
              title="Heading 3"
            >
              <Heading3 className="h-4 w-4" />
            </ToolbarButton>
            <div className="w-px h-6 bg-[var(--border)] mx-1" />
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              isActive={editor.isActive('bulletList')}
              title="Bullet List"
            >
              <List className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              isActive={editor.isActive('orderedList')}
              title="Ordered List"
            >
              <ListOrdered className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              isActive={editor.isActive('blockquote')}
              title="Blockquote"
            >
              <Quote className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={setLink}
              isActive={editor.isActive('link')}
              title="Add Link"
            >
              <Link2 className="h-4 w-4" />
            </ToolbarButton>
            <div className="w-px h-6 bg-[var(--border)] mx-1" />
            <ToolbarButton
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              title="Undo"
            >
              <Undo className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              title="Redo"
            >
              <Redo className="h-4 w-4" />
            </ToolbarButton>
          </>
        )}

        {isHtmlMode && (
          <span className="text-[var(--text-muted)] text-sm flex items-center gap-2 px-2">
            <Code className="h-4 w-4" />
            HTML Mode - Custom Tailwind classes preserved
          </span>
        )}

        <div className="flex-1" />

        {/* Mode Toggle */}
        <button
          type="button"
          onClick={toggleMode}
          className={`px-3 py-1.5 rounded text-sm font-medium flex items-center gap-2 transition-colors ${
            isHtmlMode
              ? 'bg-[var(--primary)] text-white'
              : 'bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[var(--text-main)]'
          }`}
          title={isHtmlMode ? 'Switch to Visual Editor' : 'Switch to HTML Editor'}
        >
          {isHtmlMode ? (
            <>
              <Eye className="h-4 w-4" />
              Visual
            </>
          ) : (
            <>
              <Code className="h-4 w-4" />
              HTML
            </>
          )}
        </button>
      </div>

      {/* Editor Content */}
      <div className="bg-[var(--bg-element)]">
        {isHtmlMode ? (
          <textarea
            value={htmlContent}
            onChange={(e) => handleHtmlChange(e.target.value)}
            placeholder={placeholder}
            className="w-full min-h-[400px] p-4 bg-transparent text-[var(--text-main)] font-mono text-sm focus:outline-none resize-y"
            spellCheck={false}
          />
        ) : (
          <EditorContent editor={editor} />
        )}
      </div>
    </div>
  )
}

interface ToolbarButtonProps {
  onClick: () => void
  isActive?: boolean
  disabled?: boolean
  title: string
  children: React.ReactNode
}

function ToolbarButton({ onClick, isActive, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-2 rounded transition-colors ${
        isActive
          ? 'bg-[var(--primary)]/20 text-[var(--primary)]'
          : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  )
}
